import { DEFAULT_LANGUAGE } from "@tillix/i18n";

/** "YYYYMMDD" -> "YYYY-MM-DD". */
export function compactToIsoDate(yyyymmdd: string): string {
  return `${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`;
}

/** "YYYY-MM-DD" -> "YYYYMMDD". */
export function isoToCompactDate(iso: string): string {
  return iso.replace(/-/g, "");
}

/** "HHMMSS" -> "HH:MM:SS". */
export function compactToIsoTime(hhmmss: string): string {
  return `${hhmmss.slice(0, 2)}:${hhmmss.slice(2, 4)}:${hhmmss.slice(4, 6)}`;
}

/** "HH:MM:SS" -> "HHMMSS". */
export function isoToCompactTime(iso: string): string {
  return iso.replace(/:/g, "");
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const cacheKey = `${locale}|${JSON.stringify(options)}`;
  let cached = formatters.get(cacheKey);
  if (!cached) {
    cached = new Intl.DateTimeFormat(locale, { timeZone: "UTC", ...options });
    formatters.set(cacheKey, cached);
  }
  return cached;
}

/**
 * ICU ≥ 72 separates the hour from the day period with U+202F (narrow
 * no-break space). Normalise to a plain space so labels are stable across
 * Node versions and match the Nudge merge-tag expectations.
 */
export function plainSpaces(text: string): string {
  return text.replace(/[\u202f\u00a0]/g, " ");
}

/** "YYYYMMDD" -> "Tue, Jun 16" (UTC-based; localized to `locale`). */
export function formatDateLabel(
  yyyymmdd: string,
  locale: string = DEFAULT_LANGUAGE,
): string {
  const year = Number(yyyymmdd.slice(0, 4));
  const month = Number(yyyymmdd.slice(4, 6));
  const day = Number(yyyymmdd.slice(6, 8));
  const date = new Date(Date.UTC(year, month - 1, day));
  return plainSpaces(
    formatter(locale, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date),
  );
}

/** "HHMMSS" -> "7 AM" / "12 PM" / "9:30 AM" (localized to `locale`). */
export function formatTimeLabel(
  hhmmss: string,
  locale: string = DEFAULT_LANGUAGE,
): string {
  const hour = Number(hhmmss.slice(0, 2));
  const minute = Number(hhmmss.slice(2, 4));
  const date = new Date(Date.UTC(1970, 0, 1, hour, minute));
  return plainSpaces(
    formatter(locale, {
      hour: "numeric",
      hour12: true,
      ...(minute === 0 ? {} : { minute: "2-digit" }),
    }).format(date),
  );
}

/** ("070000","120000") -> "7 AM – 12 PM". */
export function formatTimeRange(
  timeFrom: string,
  timeTo: string,
  locale: string = DEFAULT_LANGUAGE,
): string {
  return `${formatTimeLabel(timeFrom, locale)} – ${formatTimeLabel(timeTo, locale)}`;
}

/** A UTC instant's calendar date as compact YYYYMMDD. */
function toCompactDate(utc: Date): string {
  const yy = utc.getUTCFullYear();
  const mm = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(utc.getUTCDate()).padStart(2, "0");
  return `${yy}${mm}${dd}`;
}

/** Add `days` (negative to subtract) to a compact YYYYMMDD date. */
export function addCompactDays(yyyymmdd: string, days: number): string {
  const year = Number(yyyymmdd.slice(0, 4));
  const month = Number(yyyymmdd.slice(4, 6));
  const day = Number(yyyymmdd.slice(6, 8));
  return toCompactDate(new Date(Date.UTC(year, month - 1, day + days)));
}

/** The calendar date of `now` in the IANA `timeZone`, as compact YYYYMMDD. */
export function compactDateIn(
  timeZone: string,
  now: Date = new Date(),
): string {
  const parts = formatter("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  return `${part("year")}${part("month")}${part("day")}`;
}
